import React from 'react';
import { SGD, VND, THB, HKD, MYR } from '@dragonfruit/constants/currency';
import {
  BRAINTREE,
  PAY_BY_2C2P,
  NEWEB,
  NEWEB_ATM,
  NEWEB_MMK,
  TH_BANK_TRANSFER,
  BANK_TRANSFER,
  // PAYNOW,
  COD,
  UPPER_LIMIT,
  LINE_PAY,
} from '@dragonfruit/constants/paymentTypes';
import { formatPriceWithCurrency } from '@dragonfruit/utils/priceParser';
import { calculatePriceDifference } from '@dragonfruit/utils/price';
import linePaySvg from '@dragonfruit/images/icons/ic_line_pay.svg';

const AVAILABLE_PAYMENT_METHODS_TYPES = [
  BRAINTREE,
  NEWEB,
  PAY_BY_2C2P,
  TH_BANK_TRANSFER,
  BANK_TRANSFER,
  // PAYNOW,
  COD,
  // TODO: follow real spec
  LINE_PAY,
];

const CUSTOM_PAYMENT_METHOD_TYPE_BY_CURRENCY = {
  [THB]: [TH_BANK_TRANSFER],
  [HKD]: [BANK_TRANSFER],
  [SGD]: [BANK_TRANSFER],
  [MYR]: [BANK_TRANSFER],
  [VND]: [BANK_TRANSFER, COD],
};

export const transformPlanList = planList => {
  const hasSelectedPlan = planList.some(({ isSelected }) => !!isSelected);

  return planList.map((plan, planIndex) => {
    const transformedPlan = transformPlan(plan);
    return {
      ...transformedPlan,
      // make the first plan to be selected as default value if there's no plan selected
      ...(!hasSelectedPlan && { isSelected: planIndex === 0 }),
    };
  });
};

function addCustomPaymentMethods(currentPaymentMethods, currency) {
  // Some of payment method type like "Bank Transfer" is not supported by Backend.
  // Therefore, we need to hardcode those payment methods.
  const [firstPaymentMethod] = currentPaymentMethods;
  const customPaymentMethodTypes =
    CUSTOM_PAYMENT_METHOD_TYPE_BY_CURRENCY[currency];

  if (!customPaymentMethodTypes) return currentPaymentMethods;

  const customPaymentMethods = customPaymentMethodTypes.map(methodType => ({
    ...firstPaymentMethod,
    methodType,
  }));

  return [...currentPaymentMethods, ...customPaymentMethods];
}

export const transformPlan = ({
  billingCyclePerMonths,
  contractPeriod,
  currency,
  description,
  displayCurrency,
  freeTrialDays,
  hasSubscription,
  id,
  isAddon,
  isContract,
  isFreeTrial,
  isSelected,
  longDescription,
  name,
  paymentMethods,
  planType,
  sellingTypes,
  questionQuota,
}) => {
  const customizedPaymentMethods = addCustomPaymentMethods(
    paymentMethods,
    currency
  );

  return {
    billingCyclePerMonths,
    contractPeriod,
    currency,
    description,
    displayCurrency,
    freeTrialDays,
    hasSubscription,
    id,
    isAddon,
    isContract,
    isFreeTrial,
    isSelected: !!isSelected,
    longDescription,
    name,
    paymentMethods: transformPaymentMethods(customizedPaymentMethods),
    planType,
    sellingTypes,
    questionQuota,
  };
};

export function transformPaymentMethods(paymentMethods) {
  return paymentMethods.reduce(
    (
      transformResult,
      {
        currency,
        displayCurrency,
        id,
        introductoryPrice = 0,
        methodType,
        price,
        renewable,
        sku,
        needsInvoice,
      },
      paymentMethodIndex
    ) => {
      const hasIntroductoryPrice = renewable && introductoryPrice > 0;

      const {
        difference: introductoryPriceDifference,
        differencePercentage: introductoryDiscountPercentage,
        sellPriceRate: introductoryPriceRate,
      } = calculatePriceDifference(price, introductoryPrice);

      const transformedMethod = {
        displayCurrency,
        displayPrice: formatPriceWithCurrency(currency, displayCurrency, price),
        hasIntroductoryPrice,
        id,
        introductoryDiscountPercentage,
        introductoryPrice,
        introductoryPriceDifference,
        introductoryPriceRate,
        // select the first payment method by default
        isSelected: paymentMethodIndex === 0,
        methodType,
        needsInvoice,
        price,
        sku,
      };

      if (!AVAILABLE_PAYMENT_METHODS_TYPES.includes(methodType))
        return transformResult;

      // convert NEWEB into MMK and ATM
      if (methodType === NEWEB) {
        const newebTransformedMethods = [
          {
            ...transformedMethod,
            methodType: NEWEB_ATM,
          },
        ];

        if (price <= UPPER_LIMIT[NEWEB_MMK]) {
          newebTransformedMethods.unshift({
            ...transformedMethod,
            methodType: NEWEB_MMK,
          });
        }

        return transformResult.concat(newebTransformedMethods);
      }

      // mark LINE PAY to use icon as select title
      if (methodType === LINE_PAY) {
        return [
          ...transformResult,
          {
            iconTitle: <img height="16" src={linePaySvg} alt="Line Pay" />,
            ...transformedMethod,
          },
        ];
      }

      return transformResult.concat([transformedMethod]);
    },
    []
  );
}



// WEBPACK FOOTER //
// ./app/sagas/purchase/transformPlans.js